import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { useState } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp, unlessProp } from 'remote-com-shared/style/helpers';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import BgIllustration from './assets/testimonials-background-illustration.svg';
import NewBgIllustration from './assets/testimonials-bg.png';

import { transformProperties } from '@/src/domains/prismic/helpers';
import {
  tabletBreakpoint,
  fromTabletBreakpoint,
  fromDesktopBreakpoint,
} from '@/src/domains/shared/breakpoints';

const TestimonialsSection = styled.section`
  color: var(--colors-blank);
  background-color: var(--color-tone-primary, var(--newBrandColor));
  display: flex;
  ${fluidProp('padding-top', '80px', '114px')}
  position: relative;
`;

export const SectionHeader = styled.div`
  text-align: center;
`;

export const SectionHeaderTitle = styled.div`
  font-family: var(--typography-fontBrand);
  color: var(--colors-blank);
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '32px', '40px')}
  
  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SectionLabel = styled.p`
  margin: 0 auto 18px auto;
  text-align: center;

  & > small {
    ${({ theme }) => theme.typography.subHead}
    text-transform: uppercase;
    color: var(--colors-blank);
  }
`;

export const SectionGrid = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 182px;
`;

export const ClientContainer = styled.div`
  background: var(--colors-blank);
  box-shadow: 0px 12px 48px rgba(96, 101, 123, 0.24);
  border-radius: 10px;
  padding: 70px 33px 45px 28px;
  max-width: 310px;
  box-sizing: border-box;
  position: relative;
  margin-bottom: 130px;

  display: grid;
  grid-template-rows: 1fr repeat(2, min-content);
  gap: 20px;
`;

export const ClientImage = styled.div`
  position: absolute;
  top: -65px;
  left: -115px;
  right: 0;
`;

export const ClientName = styled.div`
  font-family: var(--typography-fontBrand);
  color: var(--colors-bayoux);
  ${({ theme }) => theme.typography.titleSM}
  font-weight: 600;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ClientCompany = styled.div`
  ${({ theme }) => theme.typography.body}
  color: var(--colors-bayoux);

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ClientQuote = styled.div`
  font-family: var(--typography-fontBrand);
  ${({ theme }) => theme.typography.bodyLead}
  color: var(--colors-bayoux);
  margin-top: 2px;
  margin-bottom: 20px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ClientSpokespersonWrapper = styled.div``;

export const ClientCompanyLogo = styled.span`
  img {
    max-height: 25px;
  }
`;

export const IllustrationWrapper = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 0;

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

export const Container = styled(ContentContainer)`
  z-index: 1;
`;

export const ImageWrapper = styled.div`
  border: 10px var(--colors-blank) solid;
  border-radius: 50%;
  background-color: var(--colors-blank);
  overflow: hidden;
  width: 125px;
  height: 125px;
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export const BgImageContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  svg {
    height: auto;
    color: var(--color-tone-secondary, #0261fe);
  }
`;

export function Testimonials(props) {
  const { label, title, testimonials } = transformProperties(props);

  return (
    <TestimonialsSection>
      <Container>
        <SectionHeader>
          <SectionLabel>
            <small>{label}</small>
          </SectionLabel>
          <RichText render={title} Component={SectionHeaderTitle} />
        </SectionHeader>
        <SectionGrid>
          {testimonials.map((testimonial, index) => {
            const { clientImage, quote, name, company, companyImage, role } = testimonial;
            return (
              <ClientContainer key={index}>
                <ClientImage>
                  <ImageContainer>
                    <ImageWrapper>
                      <Image
                        src={clientImage.url}
                        alt={clientImage.alt || name || ''}
                        width={clientImage.dimensions.width}
                        height={clientImage.dimensions.height}
                        objectFit="cover"
                        objectPosition="center top"
                        quality="100"
                      />
                    </ImageWrapper>
                  </ImageContainer>
                </ClientImage>
                {quote && <RichText render={quote} Component={ClientQuote} />}
                <ClientSpokespersonWrapper>
                  {name && <RichText render={name} Component={ClientName} />}
                  {role && <RichText render={role} Component={ClientCompany} />}
                </ClientSpokespersonWrapper>
                {companyImage && (
                  <ClientCompanyLogo>
                    <Image
                      src={companyImage.url}
                      alt={companyImage.alt || company || ''}
                      width={companyImage.dimensions.width}
                      height={companyImage.dimensions.height}
                      quality="100"
                    />
                  </ClientCompanyLogo>
                )}
              </ClientContainer>
            );
          })}
        </SectionGrid>
      </Container>

      <IllustrationWrapper>
        <BgImageContainer>
          <BgIllustration />
        </BgImageContainer>
      </IllustrationWrapper>
    </TestimonialsSection>
  );
}

const TestimonialsComponent = styled(TestimonialsSection)`
  --fontColor: #003d2d;

  background-image: url(${NewBgIllustration.src});
  background-color: #003284;
  background-size: cover;
  background-position: center;

  ${fromDesktopBreakpoint(css`
    padding-top: 400px;
    margin-top: -250px;
  `)}

  ${fromTabletBreakpoint(css`
    padding-bottom: 100px;
  `)}


  ${SectionHeaderTitle} , ${SectionLabel} {
    > * {
      font-family: var(--typography-fontBossa);
    }
  }

  ${SectionGrid} {
    margin: 50px 0;

    ${tabletBreakpoint(css`
      flex-direction: column;
    `)}
  }

  ${ImageWrapper} {
    width: 48px;
    height: 48px;
    border: none;
  }

  ${ClientImage} {
    position: unset;
  }

  ${ClientCompany} {
    font-size: 12px;
    color: var(--fontColor);
  }

  ${ClientQuote} {
    font-family: var(--typography-fontBossa);
    color: var(--colors-blank);
    ${({ theme }) => theme.typography.titleSM};
    line-height: 1.5;
    font-weight: 400;
    margin-top: 16px;

    ${fromTabletBreakpoint(css`
      min-height: 140px;
    `)}

    ${tabletBreakpoint(css`
      min-height: 370px;
    `)}
  }
`;

export const ClientContent = styled.div`
  ${ClientName} {
    ${({ theme }) => theme.typography.bodyMedium};
    font-family: var(--typography-fontBossa);
    color: var(--fontColor);

    > * {
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      margin: inherit;
    }
  }
`;

export const ClientCard = styled(Button)`
  && {
    max-width: 270px;
    max-height: 80px;
    padding: 16px;
    margin-bottom: 0;
    gap: 16px;
    display: flex;
    align-items: center;
    background-color: var(--colors-blank);
    box-sizing: border-box;
    border-radius: 16px;
    text-align: left;
    ${sharedTransition('background-color')};
    justify-content: flex-start;

    && {
      ${unlessProp(
        '$active',
        css`
          background-color: ${({ theme }) => theme.colors.blank_15};
          min-width: 80px;

          &:hover {
            background-color: ${({ theme }) => theme.colors.blank_45};
          }

          ${ClientContent} {
            ${fromTabletBreakpoint(css`
              display: none;
              max-width: 80px;
              gap: 0;
            `)}
          }
        `
      )}
    }
  }
`;

export function NewTestimonials(props) {
  const { label, title, testimonials } = transformProperties(props);
  const [activeCard, setActiveCard] = useState(0);

  return (
    <TestimonialsComponent>
      <Container>
        <SectionHeader>
          {fieldHasContent(label) && (
            <SectionLabel>
              <small>{label}</small>
            </SectionLabel>
          )}
          {fieldHasContent(title) && (
            <SectionHeaderTitle>
              <PrismicRichText field={title} />
            </SectionHeaderTitle>
          )}
        </SectionHeader>
        <SectionGrid>
          {testimonials.map((testimonial, index) => {
            const { clientImage, name, company } = testimonial;
            return (
              <ClientCard
                key={index}
                onClick={() => setActiveCard(index)}
                $active={activeCard === index}
              >
                <ClientImage>
                  <ImageContainer>
                    <ImageWrapper>
                      <Image
                        src={clientImage?.url}
                        alt={clientImage?.alt || name || ''}
                        width={clientImage?.dimensions.width}
                        height={clientImage?.dimensions.height}
                        quality="100"
                      />
                    </ImageWrapper>
                  </ImageContainer>
                </ClientImage>
                <ClientContent>
                  {fieldHasContent(name) && (
                    <ClientName>
                      <PrismicRichText field={name} />
                    </ClientName>
                  )}
                  {fieldHasContent(company) && (
                    <ClientCompany>
                      <PrismicRichText field={company} />
                    </ClientCompany>
                  )}
                </ClientContent>
              </ClientCard>
            );
          })}

          {fieldHasContent(testimonials[activeCard]?.quote) && (
            <ClientQuote>
              <PrismicRichText field={testimonials[activeCard]?.quote} />
            </ClientQuote>
          )}
        </SectionGrid>
      </Container>
    </TestimonialsComponent>
  );
}
